import { WbSubject } from "../../workbench/types/WbTypes";

const LAST_VISITED_SUBJECT_KEY = "workbench-new:last-visited-subject";

export type LastVisitedSubject = {
    subject: WbSubject;
    viewedAt: string;
};

export const toStoredSubject = (subject: WbSubject): WbSubject => ({
    id: subject.id,
    kind: subject.kind,
    identifier: subject.identifier,
    cedent: subject.cedent ?? null,
    periodFrom: subject.periodFrom ?? null,
    periodTo: subject.periodTo ?? null,
    context: subject.context
        ? {
              cedent: subject.context.cedent ?? null,
              broker: subject.context.broker ?? null,
              claimant: subject.context.claimant ?? null,
              dateOfLoss: subject.context.dateOfLoss ?? null,
              uwYear: subject.context.uwYear ?? null,
              mainClass: subject.context.mainClass ?? null,
              currency: subject.context.currency ?? null,
          }
        : null,
    state: {
        overallStatus: subject.state.overallStatus,
        openTasks: subject.state.openTasks,
        totalTasks: subject.state.totalTasks,
        lastUpdatedAt: subject.state.lastUpdatedAt ?? null,
    },
    tasks: [...subject.tasks],
    createdAt: subject.createdAt,
    updatedAt: subject.updatedAt ?? null,
    createdBy: subject.createdBy ?? null,
    isArchived: subject.isArchived,
});

export const saveLastVisitedSubject = (subject: WbSubject) => {
    if (typeof window === "undefined") {
        return;
    }

    const payload: LastVisitedSubject = {
        subject: toStoredSubject(subject),
        viewedAt: new Date().toISOString(),
    };

    window.localStorage.setItem(
        LAST_VISITED_SUBJECT_KEY,
        JSON.stringify(payload),
    );
};

export const readLastVisitedSubject = (): LastVisitedSubject | null => {
    if (typeof window === "undefined") {
        return null;
    }

    const rawValue = window.localStorage.getItem(LAST_VISITED_SUBJECT_KEY);

    if (!rawValue) {
        return null;
    }

    try {
        const parsedValue = JSON.parse(rawValue) as LastVisitedSubject;

        if (!parsedValue?.subject?.id || !parsedValue?.viewedAt) {
            return null;
        }

        return parsedValue;
    } catch {
        return null;
    }
};

export const formatViewedAtLabel = (viewedAt?: string | null) => {
    if (!viewedAt) {
        return "Viewed recently";
    }

    const date = new Date(viewedAt);

    if (Number.isNaN(date.getTime())) {
        return "Viewed recently";
    }

    const now = new Date();
    const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
    );
    const startOfViewedDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
    );
    const daysDifference = Math.round(
        (startOfToday.getTime() - startOfViewedDay.getTime()) / 86400000,
    );

    if (daysDifference <= 0) {
        return "Viewed today";
    }

    if (daysDifference === 1) {
        return "Viewed yesterday";
    }

    return `Viewed ${date.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
    })}`;
};
